import type {ModalWindowBindings, ModalWindowResolveValues} from '../conversation';
import type {Component} from 'typings/portaro.fe.types';
import type {PromiseBasedRecordEditationDataService} from '../../../editation/record-editation';
import type RecordEditationService from '../../../editation/record-editation.service';
import type {IOnInit} from 'angular';
import {createPayloadActionResponse} from '../modal-utils';
import type {RecordEditationServiceFactory} from '../../../editation/record-edtitation-service.factory';
import type {NewRecordEditationParams} from '../../../editation/record-editation.data-service';
import type {RecordEditation} from 'typings/portaro.be.types';
import type FinishedResponseInteractionService from '../../services/finished-response-interaction.service';
import KpValueEditorForceSettingsWrapper
    from '../../value-editors/kp-value-editor-force-settings/KpValueEditorForceSettingsWrapper.svelte';

export interface RecordEditationModalModel {
    editationParams: NewRecordEditationParams;
    focusedFieldTypeId: string;
}

class RecordEditationModalComponentController implements IOnInit, ModalWindowBindings<RecordEditationModalModel> {
    public resolve: ModalWindowResolveValues<RecordEditationModalModel>;
    public kpValueEditorForceSettingsWrapper = KpValueEditorForceSettingsWrapper;

    private recordEditationService: RecordEditationService;

    /*@ngInject*/
    constructor(private recordEditationDataService: PromiseBasedRecordEditationDataService,
                private recordEditationServiceFactory: RecordEditationServiceFactory,
                private finishedResponseInteractionService: FinishedResponseInteractionService) {}

    public $onInit(): void {
        const editationParams = this.resolve.model.editationParams;
        const focusedFieldTypeId = this.resolve.model.focusedFieldTypeId;

        this.initRecordEditation(editationParams, focusedFieldTypeId);
    }

    private async initRecordEditation(editationParams: NewRecordEditationParams, focusedFieldTypeId: string) {
        try {
            const newEditation: RecordEditation = await this.recordEditationDataService.createNewEditation(editationParams);
            this.recordEditationService = this.recordEditationServiceFactory.createService(newEditation, focusedFieldTypeId);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            this.resolve.modalWindowActions.cancel();
            throw exceptionResponse;
        }
    }

    private successClose() {
        const editationObject: RecordEditation = this.recordEditationService.editation()
        const actionResponse = createPayloadActionResponse({
            id: editationObject.recordId,
            text: editationObject.text
        });
        this.resolve.modalWindowActions.submitPromise(Promise.resolve(actionResponse));
    }

    public async publishDraftAndClose() {
        await this.recordEditationService.publish();
        this.successClose();
    }

    public async deleteDraftAndClose() {
        await this.recordEditationService.remove();
        this.resolve.modalWindowActions.cancel();
    }

    public async saveAndClose() {
        await this.recordEditationService.save();
        this.successClose();
    }

    public discardAndClose() {
        this.successClose();
    }

    public async close() {
        if (this.recordEditationService.isDraft()) {
            return this.deleteDraftAndClose();
        }
        return this.discardAndClose();
    }
}

/**
 * @ngdoc component
 * @name recordEditationModal
 * @module portaro.conversation
 *
 * @description
 * Record editation in modal window
 */
// eslint-disable-next-line
export default class RecordEditationModalComponent implements Component<ModalWindowBindings<RecordEditationModalModel>> {
    public static componentName = 'recordEditationModal';

    public templateUrl = require('./record-editation-modal.tpl.ftl');
    public bindings = {
        resolve: '<'
    } as const;
    public controller = RecordEditationModalComponentController;
}
